import React from 'react';
import { ChevronDown, Search as LucideSearch } from 'lucide-react';

import { Badge } from '@dashboard/components/ui/badge';
import { SidebarMenuButton } from '@dashboard/components/ui/sidebar';
import { useCommandPalette } from '@dashboard/components/CommandPalette';
import { useIsMobile } from '@dashboard/hooks/use-mobile';

export const NAV_ITEM_CLASS = 'h-8 text-[13px] font-medium';
export const NAV_SUB_ITEM_CLASS = 'h-7 text-[13px] font-medium pl-8 translate-x-0';
export const NAV_SUB_LIST_CLASS = 'mx-0 gap-0 border-l-0 px-0 translate-x-0';

export function NavBadge({ children }) {
  if (!children) return null;
  return (
    <Badge className="ml-auto rounded-md bg-violet-500/15 px-1.5 text-violet-700 dark:bg-violet-400/20 dark:text-violet-300 group-data-[collapsible=icon]:hidden [&+svg]:ml-2">
      {children}
    </Badge>
  );
}

export function Caret({ isOpen }) {
  return (
    <ChevronDown
      aria-hidden="true"
      className={`ml-auto !size-3 text-sidebar-foreground/60 transition-transform duration-150 ${
        isOpen ? '' : '-rotate-90'
      }`}
    />
  );
}

export function SidebarSearchButton({ onClick }) {
  return (
    <SidebarMenuButton
      onClick={onClick}
      className={`${NAV_ITEM_CLASS} pl-[7px] border border-sidebar-border rounded-md text-muted-foreground hover:text-muted-foreground`}
    >
      <LucideSearch />
      <span>Search</span>
      <kbd className="ml-auto inline-flex items-center gap-px text-[11px] leading-none text-muted-foreground/80 font-normal font-sans">
        <span className="text-[11px] leading-none">⌘</span>
        <span className="text-[11px] leading-none">K</span>
      </kbd>
    </SidebarMenuButton>
  );
}

// Phones have no room in a top bar, so their search stays in the sidebar sheet.
export function TopBarSearchButton() {
  const { openPalette } = useCommandPalette();
  const isMobile = useIsMobile();
  if (isMobile) return <div />;
  return (
    <button
      type="button"
      onClick={() => openPalette('top_bar_click')}
      className="flex h-8 w-full min-w-0 items-center gap-2 rounded-lg border border-sidebar-border bg-white dark:bg-background px-3 text-sm font-normal text-muted-foreground transition-colors hover:bg-sidebar-accent"
    >
      <LucideSearch className="size-4 shrink-0" />
      <span className="truncate">Search</span>
      <kbd className="ml-auto hidden items-center gap-px font-sans text-[11px] leading-none font-normal text-muted-foreground/80 sm:inline-flex">
        <span>⌘</span>
        <span>K</span>
      </kbd>
    </button>
  );
}

// Content is capped at 80rem and centred; this starts top bar search at that column's left edge.
export const CONTENT_COLUMN_START = { marginLeft: 'max(0px, calc((100% - 80rem) / 2))' };

// Inside a page the top bar's search starts after the rail and page sidebar; these add the
// form column's 24px padding, or the overview column's 16px plus its centring on wide screens.
export const EDITOR_FORM_COLUMN_START = { marginLeft: '0.5rem' };
export const PAGE_CONTENT_COLUMN_START = {
  marginLeft: 'max(0px, calc((100% - 80rem) / 2))',
};
export const PAGE_CONTENT_COLUMN_END = { marginRight: 'max(0px, calc((100% - 80rem) / 2))' };
